import { PaperPlaneIcon } from "@radix-ui/react-icons";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { IAdminPatientListItem } from "../../../@types/admin/AdminPatient";
import { TableSorted } from "../../../components/TableSorted";
import { useSendEmedRegistration } from "../../../hooks/mutations/admin/patients";
import {
  PATIENTS_QUERY_KEY,
  useAdminPatients,
} from "../../../hooks/queries/admin/patients";
import { useDebounce } from "../../../hooks/useDebounce";
import { useModal } from "../../../hooks/useModal";
import { IAdminPatientSortBy } from "../../../services/admin/patientsService";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import {
  EmailLink,
  ErrorMessage,
  FiltersContainer,
  PageContainer,
  PageHeader,
  PageTitle,
  RowAction,
  SearchInput,
  StatusBadge,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
} from "../shared";
import { RowActions, TypeTag, TypeTags } from "./AdminPatients.styles";
import { HospitalWardFilter } from "./HospitalWardFilter";
import { passwordSetTone } from "./patientListFields";
import { SendEmedRegistrationModal } from "./SendEmedRegistrationModal";

const DEFAULT_PAGE_SIZE = 10;

/** How many ward chips a row draws before collapsing the rest (EN-5164's rule). */
const VISIBLE_WARDS = 3;

/** Column id → the API's sort key. Columns not listed are not sortable. */
const SORT_KEYS: Record<string, IAdminPatientSortBy> = {
  id: "Id",
  name: "Name",
  email: "Email",
  mrn: "Mrn",
  hospital: "Hospital",
  wards: "Ward",
  created: "Created",
  lastUsed: "LastUsed",
  archived: "Archived",
};

const DEFAULT_SORTING: SortingState = [{ id: "id", desc: false }];

/**
 * Patients admin page (EN-5169), migrated from the Retool Patients app's
 * `Patients` screen. The detail view is `/admin/patients/:patientId`.
 *
 * ⚠️ The most sensitive page in the migration: the detail view edits the
 * patient record and the linked account, and can change a patient's condition
 * and reset their password. Gated by `patients:access`, which is granted per
 * user and to no role.
 *
 * Server-paginated with server-side search. Retool sent no page parameters at
 * all, so it fetched every patient in the region and its search box filtered
 * only the rows already on screen — a patient who was not on the current page
 * simply could not be found. `search` was added to `GET /Retool/Patients` for
 * this page.
 *
 * EN-5277: the hospital/ward filters, search by patient id, and sorting moved
 * to the server. Sorting in the browser reordered only the page on screen, so
 * "sort by ward" never brought a ward's patients together.
 */
export function AdminPatientsList() {
  document.title = "Patients - Admin";

  const navigate = useNavigate();
  const { openModal } = useModal();
  const { isSendingEmed } = useSendEmedRegistration();

  const [searchTerm, setSearchTerm] = useState("");
  const [hospitalId, setHospitalId] = useState("");
  const [wardId, setWardId] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);

  // Debounced so typing a name is one request, not one per keystroke. The
  // Measurements page uses an explicit Find button instead because its read is
  // far heavier; a page of patients is cheap enough to search as you type.
  const debouncedSearch = useDebounce(searchTerm, 400);

  const sort = sorting[0] ?? DEFAULT_SORTING[0];

  const { patients, totalCount, isFetching, isError } = useAdminPatients({
    page,
    pageSize,
    search: debouncedSearch,
    hospitalGroupId: hospitalId ? Number(hospitalId) : undefined,
    wardId: wardId ? Number(wardId) : undefined,
    sortBy: SORT_KEYS[sort.id] ?? "Id",
    sortDescending: sort.desc,
  });

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    // A new search has to start at page 1 or it can land past the end of the
    // narrowed result set and show nothing.
    setPage(1);
  };

  const handleHospitalChange = (value: string) => {
    setHospitalId(value);
    // The ward list is the hospital's, so a ward from another one is stale.
    setWardId("");
    setPage(1);
  };

  const handleWardChange = (value: string) => {
    setWardId(value);
    setPage(1);
  };

  const handleSortingChange = (next: SortingState) => {
    // Only server-sortable columns are sortable in the table, so an empty
    // state (third click) falls back to the default rather than "unsorted".
    setSorting(next.length > 0 ? next : DEFAULT_SORTING);
    setPage(1);
  };

  const columns: ColumnDef<IAdminPatientListItem, string>[] = useMemo(
    () => [
      {
        id: "id",
        header: "ID",
        accessorFn: (patient) => String(patient.id),
        enableSorting: true,
      },
      { accessorKey: "name", header: "Name", enableSorting: true },
      {
        accessorKey: "email",
        header: "Email",
        cell: ({ row }) => (
          <EmailLink
            href={`mailto:${row.original.email}`}
            onClick={(e) => e.stopPropagation()}
          >
            {row.original.email}
          </EmailLink>
        ),
        enableSorting: true,
      },
      { accessorKey: "mrn", header: "MRN", enableSorting: true },
      { accessorKey: "hospital", header: "Hospital", enableSorting: true },
      {
        // EN-4960: a patient can be in several wards. Retool showed both a
        // single "Ward" column (the primary) and a "Wards" tag list; one
        // column carrying the set, with the primary first, says the same
        // thing without the two disagreeing.
        id: "wards",
        header: "Wards",
        accessorFn: (patient) =>
          (patient.wards ?? []).map((ward) => ward.wardName).join(", "),
        cell: ({ row }) => {
          const wards = row.original.wards ?? [];
          if (wards.length === 0) {
            return <span>{row.original.ward ?? "-"}</span>;
          }
          const overflow = wards.length - VISIBLE_WARDS;
          return (
            <TypeTags>
              {wards.slice(0, VISIBLE_WARDS).map((ward) => (
                <TypeTag key={ward.wardId}>
                  {ward.wardName}
                  {ward.isPrimary ? " (primary)" : ""}
                </TypeTag>
              ))}
              {overflow > 0 && (
                <TypeTag
                  title={wards
                    .slice(VISIBLE_WARDS)
                    .map((ward) => ward.wardName)
                    .join("\n")}
                >
                  +{overflow} more
                </TypeTag>
              )}
            </TypeTags>
          );
        },
        // Sorted by the primary ward on the server — the set has no order.
        enableSorting: true,
      },
      {
        accessorKey: "created",
        header: "Created",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.created, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "lastUsed",
        header: "Last used",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.lastUsed, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        // Three states, not a bool: "Set" / "Not set" / "No data added".
        id: "passwordSet",
        header: "Password",
        accessorFn: (patient) => patient.passwordSet || "-",
        cell: ({ row }) => (
          <StatusBadge status={passwordSetTone(row.original.passwordSet)}>
            {row.original.passwordSet || "-"}
          </StatusBadge>
        ),
        // Derived in the query, not a column the server sorts on.
        enableSorting: false,
      },
      {
        // This list does not filter archived (EN-5171), so discharged patients
        // appear here. Saying so beats letting them pass as active — and the
        // detail view withholds the credential actions for them either way.
        id: "archived",
        header: "State",
        accessorFn: (patient) => (patient.archived ? "Archived" : "Active"),
        cell: ({ row }) => (
          <StatusBadge status={row.original.archived ? "inactive" : "active"}>
            {row.original.archived ? "Archived" : "Active"}
          </StatusBadge>
        ),
        enableSorting: true,
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions>
            {/* Retool hid this action unless the patient's hospital group has
                the integration enabled, and fired it straight off the click
                behind Retool's own generic confirm. Kept hidden, and given a
                real confirmation naming the patient.

                Also hidden for an archived patient: registering a discharged
                patient on the medication feed is an enrolment action, and
                Retool's archived page had no eMED action at all (EN-5171). */}
            {row.original.emedIntegrationEnabled && !row.original.archived && (
              <RowAction
                label="Register eMED"
                icon={<PaperPlaneIcon />}
                disabled={isSendingEmed}
                onClick={() => {
                  openModal(
                    <SendEmedRegistrationModal patient={row.original} />,
                    { width: "520px", showCloseButton: true }
                  );
                }}
              />
            )}
          </RowActions>
        ),
      },
    ],
    [isSendingEmed]
  );

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const pagination = {
    currentPage: page,
    pageSize,
    totalPages,
    totalItems: totalCount,
    hasNextPage: page < totalPages,
    hasPreviousPage: page > 1,
  };

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Patients</PageTitle>
      </PageHeader>

      <FiltersContainer>
        <HospitalWardFilter
          idPrefix="admin-patients"
          hospitalId={hospitalId}
          wardId={wardId}
          onHospitalChange={handleHospitalChange}
          onWardChange={handleWardChange}
        />
        <SearchInput
          type="text"
          placeholder="Search by ID, name, email, MRN, ward or hospital..."
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
      </FiltersContainer>

      {isError && (
        <ErrorMessage>
          Failed to load the patient list. Try refreshing the page.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper refreshKeys={[[PATIENTS_QUERY_KEY]]}>
          <TableSorted
            columns={columns}
            data={patients}
            emptyStateText={
              isFetching ? <TableLoadingState /> : "No rows found"
            }
            onRowClick={(patient) => navigate(`/admin/patients/${patient.id}`)}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            manualSorting
          />
        </TableWrapper>
        <TablePagination
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          exportCsv={{
            filename: "patients",
            columns,
            // Server-paginated: only the fetched page can be written, and the
            // button's row count says so.
            rows: patients,
            currentPageOnly: true,
          }}
        />
      </TableContainer>
    </PageContainer>
  );
}
